import type {
  ParsedCvData,
  TailoredCVOverrides,
  TailoredEducationItem,
  TailoredCertificationItem,
  TailoredExperienceItem,
  TailoredHighlightItem,
} from "@tabbio/types";
import { normalizeCredentialUrl } from "@tabbio/utils";
import { generateId, parsePickerDate, parsePeriodString } from "./cv-mapping";
import type {
  CVEducation,
  CVCertification,
  CVExperience,
  CVHighlight,
} from "./cv-mock-data";

function toIso(dateOnly: string | null): string | null {
  return dateOnly ? new Date(`${dateOnly}T00:00:00.000Z`).toISOString() : null;
}

function indexById<T extends { id: string }>(items: T[]): Map<string, T> {
  return new Map(items.map((item) => [item.id, item]));
}

export function toEducationSnapshot(
  items: CVEducation[],
  originals: TailoredEducationItem[] = [],
): TailoredEducationItem[] {
  const masterById = indexById(originals);
  return items.map((edu) => {
    const original = masterById.get(edu.id);
    return {
      id: edu.id,
      school: edu.school,
      degree: edu.degree || null,
      // `fieldOfStudy` + `startDate` aren't surfaced in the tailored form.
      // Preserve them from the master read so the first save doesn't drop
      // structural fields the user can't even see.
      fieldOfStudy: original?.fieldOfStudy ?? null,
      startDate: original?.startDate ?? edu.startDate ?? null,
      endDate: toIso(parsePickerDate(edu.year)),
      description: edu.description || null,
    };
  });
}

export function toCertificationSnapshot(
  items: CVCertification[],
  originals: TailoredCertificationItem[] = [],
): TailoredCertificationItem[] {
  const masterById = indexById(originals);
  return items.map((c) => {
    const original = masterById.get(c.id);
    const dateStr = (c.date || "").replace(/^Issued\s+/i, "").trim();
    return {
      id: c.id,
      name: c.name,
      issuer: c.issuer,
      issueDate: toIso(parsePickerDate(dateStr)),
      // expirationDate isn't exposed in the tailored form: preserve from
      // master. credentialUrl IS editable, so prefer the edited value and fall
      // back to master when the field is left untouched.
      expirationDate: original?.expirationDate ?? null,
      credentialUrl:
        normalizeCredentialUrl(c.credentialUrl) ??
        original?.credentialUrl ??
        null,
    };
  });
}

export function toExperienceSnapshot(
  items: CVExperience[],
): TailoredExperienceItem[] {
  return items.map((e) => {
    const { startDate, endDate, current } = parsePeriodString(e.period);
    const startIso = toIso(startDate);
    return {
      id: e.id,
      title: e.role,
      company: e.company,
      location: e.location || null,
      locationType: e.locationType || null,
      employmentType: e.employmentType || null,
      startDate: startIso,
      endDate: toIso(endDate),
      current,
      overview: e.overview || null,
      bullets: e.bullets,
    };
  });
}

export function toHighlightSnapshot(
  items: CVHighlight[],
): TailoredHighlightItem[] {
  return items.map((h) => ({
    title: h.title,
    desc: h.desc,
  }));
}

// ─── Parsed-resume → Tailored CV overrides ─────────────────────

function parsedDateToIso(date: string | undefined | null): string | null {
  if (!date) return null;
  const trimmed = date.trim();
  if (!trimmed) return null;
  const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return new Date(
      `${ymd[1]}-${ymd[2]}-${ymd[3]}T00:00:00.000Z`,
    ).toISOString();
  }
  const ym = trimmed.match(/^(\d{4})-(\d{2})$/);
  if (ym) {
    return new Date(`${ym[1]}-${ym[2]}-01T00:00:00.000Z`).toISOString();
  }
  if (/^\d{4}$/.test(trimmed)) {
    return new Date(`${trimmed}-01-01T00:00:00.000Z`).toISOString();
  }
  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback.toISOString();
}

function parseLanguageEntry(raw: string): {
  language: string;
  proficiency: string;
} {
  const match = raw.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  return match
    ? { language: match[1].trim(), proficiency: match[2].trim() }
    : { language: raw.trim(), proficiency: "" };
}

function cleanOptional(value: string | undefined | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Build a TailoredCVOverrides patch from a freshly parsed resume so the
 * tailored CV is fully populated from the upload without mutating the
 * user's master profile. Mirrors the section coverage of the resolver:
 * skills/languages/experiences/educations/certifications/highlights +
 * headline. The CV row's `summary` and `title` are written separately.
 */
export function buildTailoredOverridesFromParsedCvData(
  parsed: ParsedCvData,
): Partial<Omit<TailoredCVOverrides, "version">> {
  const parsedSocials = parsed.socials ?? {};
  const phone = cleanOptional(parsed.phone);
  const socials = Object.fromEntries(
    Object.entries(parsedSocials).filter(
      ([, value]) => value.trim().length > 0,
    ),
  );
  if (phone && !socials.phone) {
    socials.phone = phone;
  }

  const overrides: Partial<Omit<TailoredCVOverrides, "version">> = {
    header: {
      name: cleanOptional(parsed.name),
      location: cleanOptional(parsed.location),
      avatarUrl: null,
      contactEmail: cleanOptional(parsed.email),
      phone,
      socials,
    },
    skillGroups:
      parsed.skills.length > 0
        ? [{ category: "Skills", items: parsed.skills }]
        : [],
    languages: parsed.languages.map(parseLanguageEntry),
    experiences: parsed.experience.map((e) => ({
      id: generateId(),
      title: e.title || "",
      company: e.company || "",
      location: e.location || null,
      locationType: e.locationType || null,
      employmentType: e.employmentType || null,
      // Unknown dates remain unknown in the saved snapshot.
      startDate: parsedDateToIso(e.startDate),
      endDate: parsedDateToIso(e.endDate),
      current: e.current,
      overview: e.description || null,
      bullets: e.achievements,
    })),
    educations: parsed.education.map((e) => ({
      id: generateId(),
      school: e.school || "",
      degree: e.degree || null,
      fieldOfStudy: e.fieldOfStudy || null,
      startDate: parsedDateToIso(e.startDate),
      endDate: parsedDateToIso(e.endDate),
      description: e.description || null,
    })),
    certifications: parsed.certifications.map((c) => ({
      id: generateId(),
      name: c.name || "",
      issuer: c.issuer || "",
      issueDate: parsedDateToIso(c.issueDate),
      expirationDate: parsedDateToIso(c.expirationDate),
      credentialUrl: c.credentialUrl || null,
    })),
    highlights: parsed.careerHighlights.map((h) => ({
      title: h.title,
      desc: h.description,
    })),
    projects: (parsed.featuredProjects ?? []).map((project) => ({
      id: generateId(),
      title: project.title,
      description: project.description ?? null,
      link: project.link ?? null,
      technologies: project.technologies ?? [],
    })),
  };

  if (parsed.title?.trim()) {
    overrides.headline = parsed.title.trim();
  }

  return overrides;
}
